import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';

import { useMultiValueInput } from './hooks/useMultiValueInput';
import { Tag } from './Tag';
import { useFilterValues } from './useFilterValues';

export interface DropdownValueInputProps {
  value: string | string[];
  onChange: (newValue: string | string[]) => void;
  disabled: boolean;
  filterDefinition: SimpleFilterDefinition;
  allowMultiple?: boolean;
}

export const MultiDropdownValueInput = ({
  value,
  onChange,
  disabled,
  filterDefinition,
}: DropdownValueInputProps) => {
  const axios = useAxios();
  const { options, isLoading, error } = useFilterValues(
    filterDefinition,
    axios
  );
  const [selectedValue, setSelectedValue] = useState('');

  const { tags, handleRemoveTag, containerRef } = useMultiValueInput({
    value,
    onChange,
    disabled,
  });

  const displayOptions = useMemo(() => {
    if (!options.length && !tags.length) return options;

    const combined = [...tags, ...options];
    return Array.from(new Set(combined));
  }, [tags, options]);

  const placeholderText = isLoading
    ? 'Loading...'
    : error
      ? error
      : 'Select value...';

  const handleSelectChange = (nextValue: string) => {
    if (!nextValue) return;

    if (tags.includes(nextValue)) {
      toast.error('Value already added');
      setSelectedValue('');
      return;
    }

    onChange([...tags, nextValue]);
    setSelectedValue('');
  };

  return (
    <div
      ref={containerRef}
      className={`flex flex-[3.5] flex-wrap items-center gap-1 rounded border px-3 py-1.5 text-sm ${
        disabled
          ? 'border-chariot-stroke bg-chariot-background-primary cursor-not-allowed opacity-90'
          : 'border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary'
      }`}
      aria-label="Filter value"
    >
      {tags.map((tag, index) => (
        <Tag
          key={`${tag}-${index}`}
          value={tag}
          onRemove={() => handleRemoveTag(index)}
          disabled={disabled}
        />
      ))}
      {!disabled && (
        <select
          value={selectedValue}
          onChange={e => handleSelectChange(e.target.value)}
          className="text-chariot-text-primary min-w-[160px] flex-1 border-0 bg-transparent outline-hidden"
          aria-label="Add filter value"
          disabled={disabled || isLoading}
        >
          <option value="">{placeholderText}</option>
          {displayOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};
